import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FilterState } from '../../types';
import { RotateCcw, Check, X } from 'lucide-react';
import { useTheme } from '../styles/ThemeContext';
interface LotFiltersProps {
  filters: FilterState;
  onFilterChange: (newFilters: FilterState) => void;
  onClose?: () => void;
}
export function LotFilters({
  filters,
  onFilterChange,
  onClose
}: LotFiltersProps) {
  const {
    isDark
  } = useTheme();
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  const toggleBlock = (block: string) => {
    const newBlocks = new Set(filters.blocks);
    if (newBlocks.has(block)) newBlocks.delete(block);else newBlocks.add(block);
    onFilterChange({
      ...filters,
      blocks: newBlocks
    });
  };
  const toggleAvailability = (status: 'available' | 'pending' | 'sold') => {
    const newAvail = new Set(filters.availability);
    if (newAvail.has(status)) newAvail.delete(status);else newAvail.add(status);
    onFilterChange({
      ...filters,
      availability: newAvail
    });
  };
  const togglePossessionDate = (date: 'immediate' | 'over90') => {
    const newPossession = new Set(filters.possessionDate);
    if (newPossession.has(date)) newPossession.delete(date);else newPossession.add(date);
    onFilterChange({
      ...filters,
      possessionDate: newPossession
    });
  };
  const toggleFacing = (dir: 'N' | 'E' | 'S' | 'W') => {
    const newFacing = new Set(filters.facing);
    if (newFacing.has(dir)) newFacing.delete(dir);else newFacing.add(dir);
    onFilterChange({
      ...filters,
      facing: newFacing
    });
  };
  const setBedrooms = (beds: number) => {
    onFilterChange({
      ...filters,
      beds: [beds, 6]
    });
  };
  const setBathrooms = (baths: number) => {
    onFilterChange({
      ...filters,
      baths: [baths, 4]
    });
  };
  const activeCount = filters.blocks.size + filters.facing.size + filters.possessionDate.size;
  const bedroomOptions = [{
    value: 3,
    label: '3+'
  }, {
    value: 4,
    label: '4+'
  }, {
    value: 5,
    label: '5+'
  }];
  const bathroomOptions = [{
    value: 2,
    label: '2'
  }, {
    value: 2.5,
    label: '2.5'
  }, {
    value: 3,
    label: '3+'
  }];
  return <div className={`absolute top-0 left-[100px] bottom-0 w-80 backdrop-blur-xl flex flex-col z-40 transition-all duration-300 ${isDark ? 'bg-slate-900/70 border-r border-white/10' : 'bg-white/70 border-r border-white/40 shadow-xl'}`} data-id="element-135">
      {/* Header */}
      <div className={`p-4 flex items-center justify-between transition-colors duration-300 ${isDark ? 'border-b border-white/10' : 'border-b border-white/40'}`} data-id="element-136">
        <h2 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-137">
          Filter{' '}
          <span className={`font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-138">
            Lots
          </span>
        </h2>
        <div className="flex items-center gap-2" data-id="element-139">
          {activeCount > 0 && <button onClick={() => onFilterChange({
          blocks: new Set(['Block 1', 'Block 2', 'Block 3']),
          availability: new Set(['available', 'pending', 'sold']),
          priceRange: [100000, 2500000],
          sqftRange: [1500, 4000],
          landSqftRange: [5000, 15000],
          beds: [4, 6],
          baths: [2.5, 4],
          facing: new Set(['N', 'E', 'S', 'W']),
          possessionDate: new Set(['immediate', 'over90'])
        })} className={`flex items-center gap-1.5 text-[10px] uppercase font-bold transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-140">
              <RotateCcw className="w-3 h-3" data-id="element-141" />
              Reset
            </button>}
          {onClose && <button onClick={onClose} className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-white/60 text-gray-500 hover:text-gray-900'}`} aria-label="Close filters" data-id="element-142">
              <X className="w-4 h-4" data-id="element-143" />
            </button>}
        </div>
      </div>

      {/* Scrollable Filters */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5 custom-scrollbar" data-id="element-144">
        {/* Availability - Horizontal */}
        <section data-id="element-145">
          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`} data-id="element-146">
            Availability
          </h3>
          <div className="flex gap-2" data-id="element-147">
            {['available', 'pending', 'sold'].map(status => {
            const isActive = filters.availability.has(status as any);
            return <button key={status} onClick={() => toggleAvailability(status as any)} className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${isActive ? isDark ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400' : 'bg-teal-500/15 border-teal-400/30 text-teal-700' : isDark ? 'bg-slate-800/50 border-white/5 text-slate-400 hover:border-white/20' : 'bg-white/50 border-white/40 text-gray-500 hover:border-white/60'}`} data-id="element-148">
                  <span className="capitalize" data-id="element-149">{status}</span>
                </button>;
          })}
          </div>
        </section>

        {/* Possession Date - 2 Buttons */}
        <section data-id="element-150">
          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`} data-id="element-151">
            Possession Date
          </h3>
          <div className="flex gap-2" data-id="element-152">
            <button onClick={() => togglePossessionDate('immediate')} className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${filters.possessionDate.has('immediate') ? isDark ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400' : 'bg-teal-500/15 border-teal-400/30 text-teal-700' : isDark ? 'bg-slate-800/50 border-white/5 text-slate-400 hover:border-white/20' : 'bg-white/50 border-white/40 text-gray-500 hover:border-white/60'}`} data-id="element-153">
              Immediate
            </button>
            <button onClick={() => togglePossessionDate('over90')} className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${filters.possessionDate.has('over90') ? isDark ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400' : 'bg-teal-500/15 border-teal-400/30 text-teal-700' : isDark ? 'bg-slate-800/50 border-white/5 text-slate-400 hover:border-white/20' : 'bg-white/50 border-white/40 text-gray-500 hover:border-white/60'}`} data-id="element-154">
              90+ days
            </button>
          </div>
        </section>

        {/* Price Range Slider */}
        <section data-id="element-155">
          <RangeSlider label="Price Range" min={100000} max={2500000} value={filters.priceRange} onChange={value => onFilterChange({
          ...filters,
          priceRange: value
        })} formatValue={(v, isMax, max) => {
          if (isMax && v === max) {
            return '$2.5M+';
          }
          if (v >= 1000000) {
            return `$${(v / 1000000).toFixed(1)}M`;
          }
          return `$${Math.round(v / 1000)}k`;
        }} isDark={isDark} data-id="element-156" />
        </section>

        {/* Land Size Slider */}
        <section data-id="element-157">
          <RangeSlider label="Land Size" min={5000} max={15000} value={filters.landSqftRange} onChange={value => onFilterChange({
          ...filters,
          landSqftRange: value
        })} formatValue={v => `${v.toLocaleString()} sqft`} isDark={isDark} data-id="element-158" />
        </section>

        {/* House Size Slider */}
        <section data-id="element-159">
          <RangeSlider label="House Size" min={1500} max={4000} value={filters.sqftRange} onChange={value => onFilterChange({
          ...filters,
          sqftRange: value
        })} formatValue={v => `${v.toLocaleString()} sqft`} isDark={isDark} data-id="element-160" />
        </section>

        {/* Bedrooms - 3 Buttons */}
        <section data-id="element-161">
          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`} data-id="element-162">
            Bedrooms
          </h3>
          <div className="flex gap-2" data-id="element-163">
            {bedroomOptions.map(option => {
            const isActive = filters.beds[0] === option.value;
            return <button key={option.value} onClick={() => setBedrooms(option.value)} className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${isActive ? isDark ? 'bg-cyan-500 border-cyan-500 text-white' : 'bg-teal-600 border-teal-600 text-white' : isDark ? 'bg-slate-800/50 border-white/5 text-slate-400 hover:border-white/20 hover:bg-slate-800' : 'bg-white/50 border-white/40 text-gray-500 hover:border-white/60 hover:bg-white/80'}`} data-id="element-164">
                  {option.label}
                </button>;
          })}
          </div>
        </section>

        {/* Bathrooms - 3 Buttons */}
        <section data-id="element-165">
          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`} data-id="element-166">
            Bathrooms
          </h3>
          <div className="flex gap-2" data-id="element-167">
            {bathroomOptions.map(option => {
            const isActive = filters.baths[0] === option.value;
            return <button key={option.value} onClick={() => setBathrooms(option.value)} className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${isActive ? isDark ? 'bg-cyan-500 border-cyan-500 text-white' : 'bg-teal-600 border-teal-600 text-white' : isDark ? 'bg-slate-800/50 border-white/5 text-slate-400 hover:border-white/20 hover:bg-slate-800' : 'bg-white/50 border-white/40 text-gray-500 hover:border-white/60 hover:bg-white/80'}`} data-id="element-168">
                  {option.label}
                </button>;
          })}
          </div>
        </section>

        {/* Direction Facing - Horizontal */}
        <section data-id="element-169">
          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`} data-id="element-170">
            Direction Facing
          </h3>
          <div className="flex gap-2" data-id="element-171">
            {['N', 'E', 'S', 'W'].map(dir => {
            const isActive = filters.facing.has(dir as any);
            return <button key={dir} onClick={() => toggleFacing(dir as any)} className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-bold transition-all ${isActive ? isDark ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400' : 'bg-teal-500/15 border-teal-400/30 text-teal-700' : isDark ? 'bg-slate-800/50 border-white/5 text-slate-400 hover:border-white/20' : 'bg-white/50 border-white/40 text-gray-500 hover:border-white/60'}`} data-id="element-172">
                  {dir}
                </button>;
          })}
          </div>
        </section>

        {/* Block - Horizontal (Moved to bottom) */}
        <section data-id="element-173">
          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`} data-id="element-174">
            Block
          </h3>
          <div className="flex gap-2" data-id="element-175">
            {['Block 1', 'Block 2', 'Block 3'].map(block => {
            const isActive = filters.blocks.has(block);
            return <button key={block} onClick={() => toggleBlock(block)} className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${isActive ? isDark ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400' : 'bg-teal-500/15 border-teal-400/30 text-teal-700' : isDark ? 'bg-slate-800/50 border-white/5 text-slate-400 hover:border-white/20' : 'bg-white/50 border-white/40 text-gray-500 hover:border-white/60'}`} data-id="element-176">
                  {block.replace('Block ', '')}
                </button>;
          })}
          </div>
        </section>
      </div>
    </div>;
}
interface RangeSliderProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatValue: (value: number, isMax?: boolean, max?: number) => string;
  isDark: boolean;
}
function RangeSlider({
  label,
  min,
  max,
  step = 1,
  value,
  onChange,
  formatValue,
  isDark
}: RangeSliderProps) {
  const [minVal, maxVal] = value;
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Number(e.target.value);
    if (newMin <= maxVal) {
      onChange([newMin, maxVal]);
    }
  };
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Number(e.target.value);
    if (newMax >= minVal) {
      onChange([minVal, newMax]);
    }
  };
  const minPercent = (minVal - min) / (max - min) * 100;
  const maxPercent = (maxVal - min) / (max - min) * 100;
  return <div data-id="element-177">
      <div className="flex justify-between mb-2" data-id="element-178">
        <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-gray-500'}`} data-id="element-179">
          {label}
        </span>
        <span className={`text-xs ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-180">
          {formatValue(maxVal, true, max)}
        </span>
      </div>

      <div className={`relative h-1.5 rounded-full mb-1 ${isDark ? 'bg-slate-800' : 'bg-gray-300/60'}`} data-id="element-181">
        {/* Active range highlight */}
        <div className={`absolute h-full rounded-full ${isDark ? 'bg-cyan-500' : 'bg-teal-600'}`} style={{
        left: `${minPercent}%`,
        width: `${maxPercent - minPercent}%`
      }} data-id="element-182" />

        {/* Min slider */}
        <input type="range" min={min} max={max} step={step} value={minVal} onChange={handleMinChange} className={`absolute w-full h-1.5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-lg ${isDark ? '[&::-webkit-slider-thumb]:border-cyan-500 [&::-moz-range-thumb]:border-cyan-500' : '[&::-webkit-slider-thumb]:border-teal-600 [&::-moz-range-thumb]:border-teal-600'}`} style={{
        zIndex: minVal > max - (max - min) / 4 ? 5 : 3
      }} data-id="element-183" />

        {/* Max slider */}
        <input type="range" min={min} max={max} step={step} value={maxVal} onChange={handleMaxChange} className={`absolute w-full h-1.5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-lg ${isDark ? '[&::-webkit-slider-thumb]:border-cyan-500 [&::-moz-range-thumb]:border-cyan-500' : '[&::-webkit-slider-thumb]:border-teal-600 [&::-moz-range-thumb]:border-teal-600'}`} style={{
        zIndex: 4
      }} data-id="element-184" />
      </div>

      <div className="flex justify-between mt-1" data-id="element-185">
        <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`} data-id="element-186">
          {formatValue(minVal)}
        </span>
        <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`} data-id="element-187">
          {formatValue(max, true, max)}
        </span>
      </div>
    </div>;
}